import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, ArrowRight, Mail, MapPin, Send, FileText, CheckCircle, CheckCircle2,
  Building2, Factory, Store, Layers, Wrench, Weight, Blocks, ShieldCheck, MessageCircle
} from "lucide-react";

import SEO from "../components/SEO";

const stats = [
  { value: "141", label: "Projects Completed" },
  { value: "30+", label: "Years Experience" },
  { value: "221", label: "Satisfied Clients" },
  { value: "140+", label: "Workers Employed" },
];

const whyChooseUs = [
  { title: "Designed for high-rise & heavy-load applications", desc: "Engineered MS scaffolding capable of handling extreme heights and loads" },
  { title: "Used in commercial towers, refineries & infrastructure", desc: "Proven track record across diverse large-scale project types" },
  { title: "Compliant with Indian safety & load standards", desc: "Adhering to all regulatory requirements and international standards" },
  { title: "Pan-India supply & execution capability", desc: "Operating across Delhi, Noida, Hyderabad and nationwide" },
];

const solutions = [
  {
    title: "High-Rise Buildings",
    subtitle: "Residential & Commercial",
    desc: "Specialized scaffolding systems for skyscrapers and multi-storey complexes with high load-bearing capacity.",
    image: "/images/assets/high-rise-project.jpeg",
    Icon: Building2,
  },
  {
    title: "Industrial Plants & Factories",
    subtitle: "Refineries & Manufacturing",
    desc: "Heavy-duty scaffolding for industrial maintenance, shutdowns, and construction at manufacturing facilities.",
    image: "/images/assets/industrial-construction.jpeg",
    Icon: Factory,
  },
  {
    title: "Commercial Complexes & Malls",
    subtitle: "Retail & Business Centers",
    desc: "Modular scaffolding solutions for large commercial spaces with quick erection and dismantling.",
    image: "/images/assets/commercial-complex.jpeg",
    Icon: Store,
  },
  {
    title: "Infrastructure & Metro Projects",
    subtitle: "Bridges & Public Works",
    desc: "Robust scaffolding systems for bridges, flyovers, metro stations and other infrastructure development.",
    image: "/images/assets/infrastructure-metro.jpeg",
    Icon: Layers,
  },
];

const features = [
  { title: "Heavy-duty MS pipes & joints", desc: "Made from high-grade mild steel for maximum strength and durability", Icon: Wrench },
  { title: "High load-bearing capacity", desc: "Engineered to support heavy loads required in large-scale construction", Icon: Weight },
  { title: "Modular systems for fast erection", desc: "Quick assembly and dismantling to minimize project downtime", Icon: Blocks },
  { title: "Regular quality checks & maintenance", desc: "Stringent quality control and on-site maintenance support", Icon: ShieldCheck },
];

const caseStudies = [
  { title: "45-Storey Commercial Tower", desc: "Supported Ajnara Panorama construction with zero safety incidents", location: "Greater Noida" },
  { title: "Industrial Scaffolding for Cement Plant", desc: "Complete scaffolding solution for Ambuja Cement Plant expansion", location: "Mundwa, Rajasthan" },
  { title: "Airport Expansion Project", desc: "DIAL Phase 3A expansion with on-time delivery and execution", location: "Delhi" },
];

const completedProjects = [
  "All India Institute of Medical Sciences (AIIMS) Kalyani",
  "Air India Hangar Nagpur",
  "Tata Chemicals Limited Andhra Pradesh",
  "Vegas Mall Dwarka",
  "My Home Villas Hyderabad",
  "Orient Paper Mill Amlai",
  "Indian Institute of Technology (IIT) Bhubaneswar",
  "Embassy Oxygen Business Park Noida",
];

const galleryImages = [
  { src: "/images/assets/safety-standards.JPG", alt: "Safety Standards" },
  { src: "/images/assets/site-execution.jpeg", alt: "Site Execution" },
  { src: "/images/assets/quality-assurance.jpeg", alt: "Quality Assurance" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScaffoldingOnHirePage() {
  const [form, setForm] = useState({ companyName: "", projectLocation: "", projectType: "", approxHeight: "", contactNumber: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.companyName,
          phone: form.contactNumber,
          email: form.email,
          subject: "Scaffolding Rental Quote",
          company: form.companyName,
          message: `Project Type: ${form.projectType}, Location: ${form.projectLocation}, Height: ${form.approxHeight}`,
          source_page: "scaffolding-on-hire",
        }),
      });
      setSubmitted(true);
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="scaffolding-on-hire-page">
      <SEO title="Scaffolding on Hire" description="Heavy-duty scaffolding on hire for high-rise & industrial projects. Engineered MS scaffolding systems for commercial towers, factories & infrastructure across India." canonical="/scaffolding-on-hire" />
      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img alt="Heavy-duty scaffolding" className="w-full h-full object-cover" src="/images/assets/hero-scaffolding.jpeg" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            <div className="inline-flex items-center px-4 py-2 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-full text-yellow-300 text-sm font-medium">
              <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse" />
              30+ Years of Excellence
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight">
              Keep Projects Moving. <span className="text-yellow-400">Without Delays. Without Capital Block.</span>
            </h1>
            <p className="text-xl sm:text-2xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
              Rental Scaffolding solutions designed for EPCs and contractors who cannot afford execution delays or cash flow pressure.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
              <a href="#quote-form" className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-8 py-4 rounded-lg text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center" data-testid="hero-quote-btn">
                <FileText className="w-5 h-5 mr-2" />
                Request Project Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a href="tel:+919355744699" className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white hover:text-slate-900 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 hover:scale-105 inline-flex items-center">
                <Phone className="w-5 h-5 mr-2" />
                Schedule Site Inspection
              </a>
            </div>
            <div className="pt-12 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-yellow-400">{s.value}</div>
                  <div className="text-sm text-slate-400 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Why Large Contractors Choose Us</h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {whyChooseUs.map((item) => (
              <div key={item.title} className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-8 h-8 text-yellow-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((img) => (
              <div key={img.alt} className="relative h-64 rounded-lg overflow-hidden group">
                <img alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" src={img.src} loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent flex items-end p-4">
                  <p className="text-white font-semibold">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Our Scaffolding Solutions</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">Specialized scaffolding systems tailored for every type of large-scale construction project</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((sol) => (
              <div key={sol.title} className="group relative bg-slate-50 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="relative h-64 overflow-hidden">
                  <img alt={sol.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" src={sol.image} loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" />
                  <div className="absolute top-6 left-6 w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <sol.Icon className="w-7 h-7 text-slate-900" />
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{sol.title}</h3>
                  <p className="text-yellow-600 font-semibold mb-4">{sol.subtitle}</p>
                  <p className="text-slate-600 mb-6">{sol.desc}</p>
                  <Link to="/contact" className="inline-flex items-center border border-yellow-500 text-yellow-600 hover:bg-yellow-500 hover:text-slate-900 px-4 py-2 rounded-md text-sm font-medium transition-all group/btn">
                    Discuss Your Project
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.1) 35px, rgba(255,255,255,0.1) 70px)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Built for Height. Built for Safety.</h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">Our scaffolding systems combine engineering excellence with rigorous quality standards</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f) => (
              <div key={f.title} className="group relative bg-slate-800/50 backdrop-blur-sm p-8 rounded-xl border border-slate-700 hover:border-yellow-600 transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <f.Icon className="w-8 h-8 text-slate-900" />
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-yellow-400 transition-colors">{f.title}</h3>
                <p className="text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <div className="inline-flex items-center px-6 py-3 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-full">
              <ShieldCheck className="w-5 h-5 text-yellow-400 mr-2" />
              <span className="text-yellow-300 font-semibold">Compliant with Indian Safety & Load Standards</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote-form" className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Get a Custom Rental Proposal</h2>
            <p className="text-xl text-slate-600">Share your project details and receive a tailored quote within 24 hours</p>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            {submitted ? (
              <div className="text-center py-12" data-testid="form-success">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Quote Request Received!</h3>
                <p className="text-slate-600">We'll get back to you within 24 hours with a custom proposal.</p>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit} data-testid="quote-form">
                <div>
                  <label className="text-sm text-slate-700 font-semibold mb-2 flex items-center"><Building2 className="w-4 h-4 mr-2 text-yellow-600" />Company Name *</label>
                  <input className="w-full h-12 border border-slate-300 rounded-md px-3 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none" required placeholder="Your company name" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-slate-700 font-semibold mb-2 flex items-center"><MapPin className="w-4 h-4 mr-2 text-yellow-600" />Project Location *</label>
                  <input className="w-full h-12 border border-slate-300 rounded-md px-3 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none" required placeholder="City, State" value={form.projectLocation} onChange={(e) => setForm({ ...form, projectLocation: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-slate-700 font-semibold mb-2 block">Project Type *</label>
                  <select className="w-full h-12 border border-slate-300 rounded-md px-3 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none bg-white" required value={form.projectType} onChange={(e) => setForm({ ...form, projectType: e.target.value })} data-testid="project-type-select">
                    <option value="">Select project type</option>
                    <option value="High-Rise Residential Tower">High-Rise Residential Tower</option>
                    <option value="Commercial Complex">Commercial Complex</option>
                    <option value="Industrial Plant/Refinery">Industrial Plant/Refinery</option>
                    <option value="Infrastructure Project">Infrastructure Project</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-700 font-semibold mb-2 block">Approx. Height / Floors</label>
                  <input className="w-full h-12 border border-slate-300 rounded-md px-3 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none" placeholder="e.g., 20 floors or 60 meters" value={form.approxHeight} onChange={(e) => setForm({ ...form, approxHeight: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-slate-700 font-semibold mb-2 flex items-center"><Phone className="w-4 h-4 mr-2 text-yellow-600" />Contact Number *</label>
                  <input className="w-full h-12 border border-slate-300 rounded-md px-3 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none" required placeholder="+91 XXXXX XXXXX" type="tel" value={form.contactNumber} onChange={(e) => setForm({ ...form, contactNumber: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-slate-700 font-semibold mb-2 flex items-center"><Mail className="w-4 h-4 mr-2 text-yellow-600" />Email Address</label>
                  <input className="w-full h-12 border border-slate-300 rounded-md px-3 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none" placeholder="your.email@company.com" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <button className="w-full h-14 bg-yellow-500 hover:bg-yellow-600 text-slate-900 text-lg font-semibold rounded-md shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center justify-center" type="submit" disabled={submitting} data-testid="submit-quote-btn">
                  <Send className="w-5 h-5 mr-2" />
                  {submitting ? "Submitting..." : "Get Quote in 24 Hours"}
                </button>
              </form>
            )}
            <div className="mt-8 text-center">
              <p className="text-sm text-slate-500">Your information is secure and will only be used to provide you with a quote</p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Trusted on Large-Scale Projects</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">Proven track record with leading contractors across India</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {caseStudies.map((cs) => (
              <div key={cs.title} className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{cs.title}</h3>
                <p className="text-slate-600 mb-4">{cs.desc}</p>
                <div className="flex items-center text-sm text-slate-500">
                  <MapPin className="w-4 h-4 mr-1" />{cs.location}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-slate-900 text-white rounded-2xl p-8 md:p-12">
            <h3 className="text-3xl font-bold mb-8 text-center">Notable Completed Projects</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {completedProjects.map((p) => (
                <div key={p} className="flex items-start space-x-3 p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                  <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0px)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Planning a Large Project?</h2>
          <h3 className="text-3xl sm:text-4xl font-bold text-yellow-400 mb-8">Let's Build It Safely.</h3>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-12">Get expert consultation for your scaffolding requirements. Our team is ready to support your next big project.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a href="tel:+919355744699" className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-10 py-5 rounded-lg text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center" data-testid="cta-call">
              <Phone className="w-5 h-5 mr-2" />
              Call Now: 9355744699
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a href="https://wa.me/919355744699?text=Hi%2C%20I%27m%20interested%20in%20scaffolding%20rental%20for%20my%20project." target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-10 py-5 rounded-lg text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center" data-testid="cta-whatsapp">
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Consultation
            </a>
            <a href="mailto:info@amarpreet.in" className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white hover:text-slate-900 px-10 py-5 rounded-lg text-lg font-semibold transition-all duration-300 hover:scale-105 inline-flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Email Us
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 border-t border-slate-700">
            {[
              { value: "24 Hours", label: "Response Time" },
              { value: "Pan-India", label: "Service Coverage" },
              { value: "30+ Years", label: "Industry Experience" },
              { value: "Zero", label: "Compromise on Safety" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{s.value}</div>
                <div className="text-slate-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
